import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  Alert,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Info as InfoIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  DeleteForever as DeleteIcon,
  PhotoCamera as CameraIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

const GOLD = "#D4AF37";

const ManagePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacyOption: 'public',
    is_active: true,
    post_permission: 'admin_only',
    invitePermission: 'user',
    activityPermission: 'user',
    photoPermission: 'user',
    albumPermission: 'user',
    group_image: null,
    cover_photo: null,
  });

  const [activeSection, setActiveSection] = useState('Details');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const token = localStorage.getItem('jwttoken');

  const sidebarItems = [
    { label: 'Details', icon: <InfoIcon /> },
    { label: 'Settings', icon: <SettingsIcon /> },
    { label: 'Members', icon: <PeopleIcon /> },
    { label: 'Delete Page', icon: <DeleteIcon /> }
  ];

  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        const response = await axios.get(
          `https://tdtlworld.com/dss-kaas-backend/socialapp/groups/${id}/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = response.data;
        setFormData(prev => ({
          ...prev,
          name: data.name || '',
          description: data.description || '',
          privacyOption: data.privacy_option || 'public',
          invitePermission: data.invite_permission || 'user',
          activityPermission: data.activity_permission || 'user',
          photoPermission: data.photo_permission || 'user',
          albumPermission: data.album_permission || 'user',
          created_by: data.created_by,
        }));
      } catch (error) {
        console.error('Error fetching group data:', error);
      }
    };
    fetchGroupData();
  }, [id, token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        [field]: file,
      }));
    }
  };

  const handleSaveChanges = async (updateType) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);

      if (updateType === 'details') {
        if (formData.group_image instanceof File) {
          formDataToSend.append('group_image', formData.group_image);
        }
        if (formData.cover_photo instanceof File) {
          formDataToSend.append('cover_photo', formData.cover_photo);
        }
      } else if (updateType === 'settings') {
        formDataToSend.append('privacy_option', formData.privacyOption);
        formDataToSend.append('invite_permission', formData.invitePermission);
        formDataToSend.append('activity_permission', formData.activityPermission);
        formDataToSend.append('photo_permission', formData.photoPermission);
        formDataToSend.append('album_permission', formData.albumPermission);
      }

      await axios.put(
        `https://tdtlworld.com/dss-kaas-backend/socialapp/groups/${id}/`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      Swal.fire('Success', 'Group updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating group:', error);
      Swal.fire('Error', 'Failed to update group.', 'error');
    }
  };

  const handleDeleteGroup = async () => {
    try {
      const { isConfirmed } = await Swal.fire({
        title: 'Are you sure?',
        text: 'This action is irreversible. All group data will be deleted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      });

      if (isConfirmed) {
        await axios.delete(`https://tdtlworld.com/dss-kaas-backend/socialapp/groups/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        Swal.fire('Deleted!', 'The group has been deleted.', 'success');
        navigate('/group');
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      Swal.fire('Error', 'Failed to delete group.', 'error');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, py: 2 }}>
      {/* Sidebar */}
      <Paper 
        elevation={0}
        sx={{ 
          width: { xs: '100%', md: 240 },
          flexShrink: 0,
          borderRadius: 4,
          border: `1px solid ${alpha(GOLD, 0.2)}`,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          alignSelf: 'flex-start'
        }}
      >
        <List component="nav" sx={{ p: 1 }}>
          {sidebarItems.map((item) => (
            <ListItem 
              button 
              key={item.label}
              onClick={() => setActiveSection(item.label)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: activeSection === item.label ? alpha(GOLD, 0.1) : 'transparent',
                color: activeSection === item.label ? GOLD : 'text.primary',
                '&:hover': {
                  bgcolor: activeSection === item.label ? alpha(GOLD, 0.15) : alpha(GOLD, 0.05),
                }
              }}
            >
              <ListItemIcon sx={{ color: activeSection === item.label ? GOLD : 'text.secondary', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{ fontWeight: activeSection === item.label ? 700 : 500, fontFamily: "'Outfit', sans-serif" }} 
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Main Content */}
      <Paper 
        elevation={0} 
        sx={{ 
          flexGrow: 1, 
          borderRadius: 4, 
          border: `1px solid ${alpha(GOLD, 0.2)}`,
          p: 4,
          bgcolor: 'background.paper'
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, fontFamily: "'Outfit', sans-serif" }}>
          {activeSection}
        </Typography>

        {activeSection === 'Details' && (
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Group Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
            />
            
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Group Image</Typography>
                <Box sx={{ border: `1px dashed ${alpha(GOLD, 0.4)}`, borderRadius: 2, p: 2, textAlign: 'center' }}>
                  {formData.group_image instanceof File && (
                    <Box sx={{ mb: 2 }}>
                      <img 
                        src={URL.createObjectURL(formData.group_image)} 
                        alt="Group" 
                        style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }} 
                      />
                    </Box>
                  )}
                  <Button variant="outlined" component="label" startIcon={<CameraIcon />} sx={{ color: GOLD, borderColor: GOLD, '&:hover': { borderColor: '#B8860B', bgcolor: alpha(GOLD, 0.05) } }}>
                    Upload Image
                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, 'group_image')} />
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Cover Photo</Typography>
                <Box sx={{ border: `1px dashed ${alpha(GOLD, 0.4)}`, borderRadius: 2, p: 2, textAlign: 'center' }}>
                  {formData.cover_photo instanceof File && (
                    <Box sx={{ mb: 2 }}>
                      <img 
                        src={URL.createObjectURL(formData.cover_photo)} 
                        alt="Cover" 
                        style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8 }} 
                      />
                    </Box>
                  )}
                  <Button variant="outlined" component="label" startIcon={<CameraIcon />} sx={{ color: GOLD, borderColor: GOLD, '&:hover': { borderColor: '#B8860B', bgcolor: alpha(GOLD, 0.05) } }}>
                    Upload Cover
                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, 'cover_photo')} />
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="contained" 
                onClick={() => handleSaveChanges('details')}
                sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700, px: 4, py: 1.2, '&:hover': { bgcolor: '#B8860B' } }}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        )}

        {activeSection === 'Settings' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontFamily: "'Outfit', sans-serif" }}>Privacy Options</FormLabel>
              <RadioGroup name="privacyOption" value={formData.privacyOption} onChange={handleChange}>
                <FormControlLabel value="public" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="This is a public group" />
                <FormControlLabel value="private" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="This is a private group" />
                <FormControlLabel value="hidden" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="This is a hidden group" />
              </RadioGroup>
            </FormControl>
            <Divider />

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontFamily: "'Outfit', sans-serif" }}>Group Invitations</FormLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Which members of this group are allowed to invite others?</Typography>
              <RadioGroup row name="invitePermission" value={formData.invitePermission} onChange={handleChange}>
                <FormControlLabel value="user" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="All Members" />
                <FormControlLabel value="admin" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="Organizers Only" />
              </RadioGroup>
            </FormControl>
            <Divider />

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontFamily: "'Outfit', sans-serif" }}>Activity Feeds</FormLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Which members of this group are allowed to post into the activity feed?</Typography>
              <RadioGroup row name="activityPermission" value={formData.activityPermission} onChange={handleChange}>
                <FormControlLabel value="user" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="All Members" />
                <FormControlLabel value="admin" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="Organizers Only" />
              </RadioGroup>
            </FormControl>
            <Divider />

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontFamily: "'Outfit', sans-serif" }}>Group Photos</FormLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Which members of this group are allowed to upload photos?</Typography>
              <RadioGroup row name="photoPermission" value={formData.photoPermission} onChange={handleChange}>
                <FormControlLabel value="user" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="All Members" />
                <FormControlLabel value="admin" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="Organizers Only" />
              </RadioGroup>
            </FormControl>
            <Divider />

             <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontFamily: "'Outfit', sans-serif" }}>Group Albums</FormLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Which members of this group are allowed to create albums?</Typography>
              <RadioGroup row name="albumPermission" value={formData.albumPermission} onChange={handleChange}>
                <FormControlLabel value="user" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="All Members" />
                <FormControlLabel value="admin" control={<Radio sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />} label="Organizers Only" />
              </RadioGroup>
            </FormControl>

             <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="contained" 
                onClick={() => handleSaveChanges('settings')}
                sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700, px: 4, py: 1.2, '&:hover': { bgcolor: '#B8860B' } }}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        )}

        {activeSection === 'Members' && (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Manage group members; promote to moderators, co-organizers, or demote or ban.
            </Typography>
            
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 1, fontFamily: "'Outfit', sans-serif" }}>Organizers</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Organizers have total control over the contents and settings of a group. That includes all the abilities of moderators, as well as the ability to turn group forums on or off, change group status from public to private, change the group photo, manage group members, and delete the group.
            </Typography>

            <Card variant="outlined" sx={{ borderRadius: 3, mb: 4, bgcolor: alpha(GOLD, 0.02), borderColor: alpha(GOLD, 0.2) }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  src={formData?.created_by?.profile_pic || undefined} 
                  sx={{ width: 56, height: 56, border: `2px solid ${GOLD}` }}
                >
                  {formData?.created_by?.first_name?.charAt(0)}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formData?.created_by?.first_name} {formData?.created_by?.last_name}
                </Typography>
              </CardContent>
            </Card>

            <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 1, fontFamily: "'Outfit', sans-serif" }}>Members</Typography>
             <Typography variant="body2" color="text.secondary">
              When a member joins a group, he or she is assigned the member role by default. Members are able to contribute to the group’s discussions, activity feeds, and view other group members.
            </Typography>
          </Box>
        )}

        {activeSection === 'Delete Page' && (
          <Box>
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">Warning</Typography>
              Deleting this group will completely remove ALL content associated with it. There is no way back.
            </Alert>
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={confirmDelete} 
                  onChange={(e) => setConfirmDelete(e.target.checked)} 
                  sx={{ '&.Mui-checked': { color: '#d32f2f' } }}
                />
              }
              label="I understand the consequences of deleting this group."
              sx={{ mb: 3 }}
            />
            
            <Box>
              <Button 
                variant="contained" 
                color="error" 
                disabled={!confirmDelete}
                onClick={handleDeleteGroup}
                sx={{ px: 4, py: 1.2, fontWeight: 700, borderRadius: '50px' }}
              >
                Delete Group
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ManagePage;
